import React from 'react';
import { Box, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { Coins, Receipt, ShoppingBag } from 'phosphor-react';

import { GlassCard, GlassPageBg } from '../../../../components/glass';
import { useFinanceiroVendas } from '../../../../hooks/useFinanceiro';
import { FinanceiroPageHeader } from '../components/FinanceiroPageHeader';
import { brl, fmtData } from '../components/format';
import { InfoPopover } from '../components/InfoPopover';
import { KpiCard } from '../components/KpiCard';
import { useFinanceiroFilters } from '../components/useFinanceiroFilters';

export const FinanceiroVendasPage = () => {
  const f = useFinanceiroFilters();
  const { data, isError } = useFinanceiroVendas({ ...f.apiParams });

  const itens = data?.itens ?? [];

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <FinanceiroPageHeader
          eyebrow="Comercial"
          titulo="Vendas"
          subtitulo="Vendas do Conta Azul no período (por data de emissão)"
          icon={<ShoppingBag size={24} weight="fill" />}
          cor="#2563EB"
          info={
            <Text>
              Vendas emitidas no período/franquia. KPIs de <b>faturamento</b>, <b>quantidade</b> e{' '}
              <b>ticket médio</b>; a lista traz as 100 vendas mais recentes.
            </Text>
          }
        />

        {isError && (
          <GlassCard intensity="medium" p={6} mb={5} tint="#DC2626">
            <Text color="#71717A">Não foi possível carregar as vendas.</Text>
          </GlassCard>
        )}

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
          <KpiCard
            label="Faturamento"
            valor={data?.total_valor ?? 0}
            color="#2563EB"
            icon={<ShoppingBag size={20} weight="duotone" />}
            index={0}
            info={
              <InfoPopover title="Faturamento">
                Soma do <i>total</i> das vendas no período/franquia.
              </InfoPopover>
            }
          />
          <KpiCard
            label="Vendas"
            valor={data?.qtd ?? 0}
            format="number"
            color="#0EA5E9"
            icon={<Receipt size={20} weight="duotone" />}
            index={1}
            info={<InfoPopover title="Vendas">Quantidade de vendas no período.</InfoPopover>}
          />
          <KpiCard
            label="Ticket médio"
            valor={data?.ticket_medio ?? 0}
            color="#16A34A"
            icon={<Coins size={20} weight="duotone" />}
            index={2}
            info={
              <InfoPopover title="Ticket médio">
                <b>Faturamento ÷ nº de vendas</b> no período.
              </InfoPopover>
            }
          />
        </SimpleGrid>

        <GlassCard intensity="strong" p={{ base: 3, md: 5 }}>
          {itens.length === 0 ? (
            <Text fontSize="sm" color="#94A3B8" textAlign="center" py={6}>
              Nenhuma venda no período.
            </Text>
          ) : (
            <Flex direction="column" gap={1}>
              <Flex
                justify="space-between"
                fontSize="10px"
                fontWeight={700}
                color="#94A3B8"
                textTransform="uppercase"
                px={2}
                pb={2}
              >
                <Text>Cliente · data</Text>
                <Text>Valor</Text>
              </Flex>
              {itens.map(v => (
                <Flex
                  key={v.id}
                  justify="space-between"
                  align="center"
                  gap={3}
                  px={2}
                  py={2}
                  borderRadius="md"
                  _hover={{ bg: 'rgba(15,23,42,0.03)' }}
                >
                  <Box minW={0}>
                    <Text fontSize="sm" fontWeight={600} color="#0F172A" noOfLines={1}>
                      {v.cliente_nome || '—'}
                    </Text>
                    <Text fontSize="11px" color="#94A3B8">
                      {fmtData(v.data)}
                      {v.numero ? ` · nº ${v.numero}` : ''}
                      {v.situacao ? ` · ${v.situacao}` : ''}
                    </Text>
                  </Box>
                  <Text fontSize="sm" fontWeight={700} color="#0F172A" whiteSpace="nowrap">
                    {brl(v.total)}
                  </Text>
                </Flex>
              ))}
            </Flex>
          )}
        </GlassCard>
      </Box>
    </GlassPageBg>
  );
};
